import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import StarRating from "@/components/StarRating";
import { MapPin, Ticket, BadgeCheck, Clock } from "lucide-react";

const PLAN_BADGE = {
  postcard: { label: "On the Postcard", cls: "bg-sam-crimson text-white" },
  premium_online: { label: "Premium", cls: "bg-sam-amber text-white" },
};

export default function BusinessCard({ b, index = 0 }) {
  const photo = b.photo_urls?.[0];
  const plan = PLAN_BADGE[b.listing_plan];
  const dealExp = b.postcard_deal_expires ? new Date(b.postcard_deal_expires) : null;
  const msLeft = dealExp ? dealExp.getTime() - Date.now() : null;
  const dealActive = b.postcard_deal_headline && (msLeft === null || msLeft > 0);
  const daysLeft = msLeft !== null ? Math.ceil(msLeft / 86400000) : null;
  const endsSoon = dealActive && daysLeft !== null && daysLeft <= 14;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.04, 0.3) }}
    >
      <Link to={`/business/${b.slug}`} data-testid={`business-card-${b.slug}`}
        className="group block rounded-xl bg-card border border-border overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
        <div className="relative h-40 bg-sam-navy/5 overflow-hidden">
          {photo ? (
            <img src={photo} alt={b.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <div className="h-full w-full grid place-items-center bg-gradient-to-br from-sam-navy to-sam-navy2 text-sam-cream/40 font-display text-3xl">
              {b.name?.[0]}
            </div>
          )}
          {plan && <span className={`absolute top-3 left-3 text-[11px] font-bold px-2.5 py-1 rounded-full ${plan.cls}`}>{plan.label}</span>}
          {b.featured_pick && <span className="absolute top-3 right-3 text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/95 text-sam-navy flex items-center gap-1"><BadgeCheck className="h-3 w-3 text-sam-crimson" />Featured</span>}
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-display font-bold text-sam-navy text-lg leading-tight truncate">{b.name}</h3>
            <span className="text-xs font-mono text-sam-slate shrink-0">{b.price_range}</span>
          </div>
          <p className="text-xs font-semibold text-sam-crimson mt-0.5">{b.category}</p>
          <div className="flex items-center gap-2 mt-1.5">
            <StarRating value={b.avg_rating} size={14} />
            <span className="text-xs text-muted-foreground">{b.avg_rating > 0 ? `${b.avg_rating} (${b.review_count})` : "New"}</span>
          </div>
          <p className="text-sm text-sam-slate mt-2 line-clamp-2">{b.short_description}</p>
          <div className="flex items-center gap-1 mt-3 text-xs text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" />{b.city}, {b.state_abbr}
          </div>
          {dealActive && (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-sam-crimson/5 border border-sam-crimson/20 p-2" data-testid={`deal-${b.slug}`}>
              <Ticket className="h-4 w-4 text-sam-crimson shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <span className="text-xs font-semibold text-sam-crimson leading-snug block">{b.postcard_deal_headline}</span>
                {endsSoon && (
                  <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-sam-amber" data-testid={`deal-ends-soon-${b.slug}`}>
                    <Clock className="h-3 w-3" />{daysLeft <= 1 ? "Ends today" : `Ends in ${daysLeft} days`}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </Link>
    </motion.div>
  );
}
